import { API_URL } from "../config/connect";

const PROFILE_CACHE_KEY = "cached_user_profile";
const PROFILE_CACHE_TTL = 5_000;
const PROFILE_TIMEOUT_MS = 10_000;

let inflight = null;

const getAuthHeaders = () => {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return null;
    return { 'Authorization': `Bearer ${accessToken}` };
};

function readCache({ allowStale = false } = {}) {
    try {
        const raw = localStorage.getItem(PROFILE_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        const fresh = typeof parsed.ts === "number" && Date.now() - parsed.ts <= PROFILE_CACHE_TTL;
        if (!fresh && !allowStale) return null;
        return parsed.data ?? null;
    } catch {
        return null;
    }
}

function writeCache(data) {
    try {
        localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
    } catch {
        // квота толған жағдайда — кэшсіз жұмыс жалғасады
    }
}

export function clearUserProfileCache() {
    try {
        localStorage.removeItem(PROFILE_CACHE_KEY);
    } catch {
        // нұқсан жоқ
    }
}

/** Ағымдағы пайдаланушы ақпараты (auth арқылы).
 *  Кэш + concurrent dedup + timeout арқылы аккаунтты тез тануды қамтамасыз етеді.
 *  @param {{ force?: boolean, signal?: AbortSignal }} [options]
 */
export async function getUserInfo(options = {}) {
    const headers = getAuthHeaders();
    if (!headers) return null;

    const { force = false, signal } = options;

    if (!force) {
        const cached = readCache();
        if (cached) return cached;
    }

    if (inflight) return inflight;

    inflight = (async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), PROFILE_TIMEOUT_MS);
        const onExternalAbort = () => controller.abort();
        if (signal) signal.addEventListener("abort", onExternalAbort);

        try {
            const response = await fetch(`${API_URL}/api/v1/user/profile`, {
                method: 'GET',
                headers,
                signal: controller.signal,
            });

            if (!response.ok) {
                if (response.status === 401) clearUserProfileCache();
                console.error(`Error fetching user info: ${response.status} ${response.statusText}`);
                return readCache({ allowStale: true });
            }

            const data = await response.json();
            writeCache(data);
            return data;
        } catch (error) {
            if (error?.name === "AbortError") {
                // timeout немесе сыртқы abort — ескі кэшке қайтамыз, болмаса null
                return readCache({ allowStale: true });
            }
            console.error("Network or unexpected error in getUserInfo:", error);
            return readCache({ allowStale: true });
        } finally {
            clearTimeout(timeoutId);
            if (signal) signal.removeEventListener("abort", onExternalAbort);
            inflight = null;
        }
    })();

    return inflight;
}

/** Профильді жаңарту (PATCH /api/v1/user/profile) — мысалы is_vat_payer */
export async function updateUserProfile(payload) {
    const headers = getAuthHeaders();
    if (!headers) throw new Error('Not authenticated');
    const response = await fetch(`${API_URL}/api/v1/user/profile`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Error ${response.status}`);
    }
    const data = await response.json();
    // Профиль өзгерген соң кэшті жаңартамыз
    try {
        const cached = readCache();
        if (cached) {
            writeCache(data?.user
                ? { ...cached, ...data }
                : { ...cached, user: { ...cached.user, ...(data || {}) } });
        }
    } catch {
        // нұқсан жоқ
    }
    return data;
}

/** Бизнес офертаны қабылдау (POST /api/v1/user/business-terms/accept) */
export async function acceptBusinessTerms() {
    const headers = getAuthHeaders();
    if (!headers) throw new Error('Not authenticated');
    const response = await fetch(`${API_URL}/api/v1/user/business-terms/accept`, {
        method: 'POST',
        headers,
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || err.message || `Error ${response.status}`);
    }
    return response.json().catch(() => ({}));
}

/** Бөтен пайдаланушы ақпараты (ID бойынша) */
export async function getUserById(userId) {
    if (!userId) throw new Error("User ID is required");

    try {
        const headers = getAuthHeaders();
        const response = await fetch(`${API_URL}/api/v1/user/${userId}`, {
            method: 'GET',
            ...(headers ? { headers } : {}),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            // Бэкенд "User not found" кезінде 500 қайтарады (404 орнына) — бэкенд багы
            const errorMsg = errorData?.error?.message || errorData?.detail || '';
            if (errorMsg.includes('not found') || errorMsg.includes('User not found')) {
                return null;
            }
            throw new Error(errorData.detail || `Error fetching user: ${response.status}`);
        }

        return response.json();
    } catch (error) {
        console.error("Failed to fetch user by ID:", error);
        throw error;
    }
}

export default getUserInfo;